import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { notificationsApi, type InvitePreviewEvent } from '../../api/notifications';
import { useAuthStore } from '../../store/authStore';
import { usePortalStore } from '../../store/portalStore';
import { getPortalUrl } from '../../lib/portal';
import type { AppNotification } from '../../types';
import { ManagerInviteModal } from './ManagerInviteModal';

// Invites the user explicitly skipped are remembered locally so the modal
// doesn't re-nag on every landing — the invite still lives in the bell.
const DISMISSED_KEY = 'pm:dismissed-invites';

function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function addDismissed(id: string): void {
  try {
    const set = readDismissed();
    set.add(id);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

/**
 * Watches for an unactioned manager invite when the user lands on the platform
 * and surfaces it as a modal (accept / decline / skip). Mounted globally for
 * any logged-in user so it works on both the creator and viewer portals.
 */
export function ManagerInvitePrompt() {
  const user = useAuthStore((s) => s.user);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const portal = usePortalStore((s) => s.portal);

  const [active, setActive] = useState<AppNotification | null>(null);
  const [preview, setPreview] = useState<InvitePreviewEvent | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actioning, setActioning] = useState<'accept' | 'decline' | null>(null);
  const checkedFor = useRef<string | null>(null);

  // On landing (once per signed-in user), look for an invite to surface.
  useEffect(() => {
    if (!user) {
      checkedFor.current = null;
      return;
    }
    if (checkedFor.current === user._id) return;
    checkedFor.current = user._id;

    let cancelled = false;
    (async () => {
      try {
        const { items } = await notificationsApi.list();
        const dismissed = readDismissed();
        const invite = items.find(
          (i) => i.type === 'manager_invite' && i.actionTaken === null && !dismissed.has(i._id),
        );
        if (cancelled || !invite) return;

        setActive(invite);
        setPreviewLoading(true);
        try {
          const { event } = await notificationsApi.managerInvitePreview(invite._id);
          if (!cancelled) setPreview(event);
        } catch {
          // Preview is best-effort — the modal still renders from invite data.
        } finally {
          if (!cancelled) setPreviewLoading(false);
        }
      } catch {
        /* listing failed — nothing to surface */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const close = useCallback(() => {
    setActive(null);
    setPreview(null);
    setPreviewLoading(false);
  }, []);

  const handleAccept = useCallback(async () => {
    if (!active) return;
    setActioning('accept');
    try {
      await notificationsApi.acceptManagerInvite(active._id);
      toast.success('Invitation accepted');
      // Accepting grants creator-portal access — refresh the session, then send
      // viewers to the creator portal where they manage the event.
      await fetchMe();
      if (portal === 'viewer') {
        window.location.href = getPortalUrl('creator');
        return;
      }
      window.dispatchEvent(new CustomEvent('pm:events-refresh'));
      close();
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      if (e.response?.status === 410) {
        toast.error(e.response.data?.error ?? 'This invitation is no longer available');
        close();
      } else {
        toast.error(e.response?.data?.error ?? 'Failed to accept invitation');
      }
    } finally {
      setActioning(null);
    }
  }, [active, fetchMe, portal, close]);

  const handleDecline = useCallback(async () => {
    if (!active) return;
    setActioning('decline');
    try {
      await notificationsApi.declineManagerInvite(active._id);
      toast.success('Invitation declined');
      close();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error ?? 'Failed to decline invitation');
    } finally {
      setActioning(null);
    }
  }, [active, close]);

  const handleSkip = useCallback(() => {
    if (actioning) return;
    if (active) addDismissed(active._id);
    close();
    toast('Saved to your notification bell — open it anytime to respond.', { icon: '🔔' });
  }, [actioning, active, close]);

  if (!active) return null;

  return (
    <ManagerInviteModal
      isOpen={Boolean(active)}
      notification={active}
      preview={preview}
      previewLoading={previewLoading}
      actioning={actioning}
      onAccept={handleAccept}
      onDecline={handleDecline}
      onSkip={handleSkip}
    />
  );
}
